/**
 * Reference-list utilities for truck PO / Order Number fields
 *
 * Outbound trucks routinely carry multiple orders, entered as comma-separated lists in the
 * PO and Order Number fields. The two lists are POSITIONALLY PAIRED — po[i] belongs to
 * order_number[i] — and the same PO legitimately repeats when several orders ship against
 * it. Nothing here reorders, sorts, or dedupes; doing so would break that pairing.
 *
 * Logiwa trucks are unaffected: they carry exactly one order, so their lists are single
 * entries and every helper below is a no-op on them.
 *
 * See Multi_Order_Outbound_Trucks.md.
 */

// Mirrors outbound_truck_schedule.po / .order_number (both VARCHAR(255)) and
// REF_LIST_MAX_LENGTH in server/controllers/outboundTruckScheduleController.js.
// Keep all three in sync.
const REF_LIST_MAX_LENGTH = 255;

// inbound_truck_schedule.po is a narrower VARCHAR(115) and was not touched by the outbound
// multi-order work. Shared editors that serve both modes must use the right bound per mode.
const INBOUND_PO_MAX_LENGTH = 115;

// Helper text shown under the PO / Order Number inputs.
const REF_LIST_HELPER_TEXT = 'Separate multiple values with commas';

/**
 * Split a reference list into its entries. Trims each, drops empties, preserves order and
 * duplicates. Returns [] for null/undefined/blank.
 */
const parseRefList = (value) => {
  if (value === null || value === undefined) return [];
  return String(value)
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
};

/**
 * How many entries a reference list holds.
 */
const countRefList = (value) => parseRefList(value).length;

/**
 * Whether a value holds more than one entry — i.e. this is a multi-order truck.
 */
const isMultiRef = (value) => countRefList(value) > 1;

/**
 * Compact label for inline badges, where a full 5-order list would blow out the layout.
 * One entry renders as-is; several collapse to "first +N". Pair with `formatRefListFull`
 * as the title attribute so the whole list stays one hover away.
 *
 *   "4567743588"                          -> "4567743588"
 *   "4567743588, 4567806025, 4567824402"  -> "4567743588 +2"
 */
const formatRefListCompact = (value) => {
  const entries = parseRefList(value);
  if (entries.length === 0) return '';
  if (entries.length === 1) return entries[0];
  return `${entries[0]} +${entries.length - 1}`;
};

/**
 * Full list on one line, normalized spacing. Use for tooltips and detail views.
 */
const formatRefListFull = (value) => parseRefList(value).join(', ');

export {
  REF_LIST_MAX_LENGTH,
  INBOUND_PO_MAX_LENGTH,
  REF_LIST_HELPER_TEXT,
  parseRefList,
  countRefList,
  isMultiRef,
  formatRefListCompact,
  formatRefListFull
};
